import React, {forwardRef, useState} from 'react';
import PropTypes from "prop-types";
import {
    DndContext,
    rectIntersection,
    MouseSensor,
    TouchSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay
} from "@dnd-kit/core";

import AnswerOptions from './AnswerOptions';
import DnDDroppableItem from './DnDDroppableItem';
import DnDDraggedItem from "./DnDDraggedItem";

import './drag_n_drop_puzzle.scss';

function DragNDropPuzzle(
    {
        component_data,
        correct_answer = {
            text: '',
            answer_selections: []
        },
        onCorrectAnswerChange
    }
) {
    const [answer, setAnswer] = useState(!!correct_answer && !Array.isArray(correct_answer) ? correct_answer : defaultAnswer());
    const [dragged_item, setDraggedItem] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
                delay: 100,
                tolerance: 5
            }
        }),
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
                delay: 100,
                tolerance: 5
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 50,
                tolerance: 5,
            }
        })
    );

    function defaultAnswer() {
        return {
            text: '',
            answer_selections: []
        }
    }

    function getAnswerText(answer_selections) {
        const answer_options = _.get(component_data, 'data.answer_options');
        const answer_layout = _.get(component_data, 'data.answer_layout');
        const answerTextArray = answer_selections.map(([layoutId, optionId]) => {
            const optionItem = _.find(answer_options, {dnd_id: optionId});
            const layoutItem = _.find(answer_layout, {dnd_id: layoutId});
            return _.get(layoutItem, 'content.text') + _.get(optionItem, 'content.text');
        });

        return answerTextArray.join('\n');
    }

    function onDragStart({active}) {
        setDraggedItem(active.dnd_id);
    }

    function onDragEnd({active, over}) {
        const draggable_id = _.get(active, 'id');
        const dest_droppable_id = _.get(over, 'id');
        const source_droppable_id = _.get(active, 'data.current.type');

        if (!draggable_id || !dest_droppable_id) {
            return null;
        }

        if (dest_droppable_id === source_droppable_id) {
            return null;
        }

        const answer_clone = _.cloneDeep(answer);

        // Moved answer back to options area
        if (dest_droppable_id === 'option-droppable') {
            return false;
        }

        // Moved answer from options area to answer area
        _.remove(answer_clone.answer_selections, (pair) => pair[0] === dest_droppable_id);
        answer_clone.answer_selections.push([dest_droppable_id, draggable_id]);
        answer_clone.answer_selections = _.orderBy(answer_clone.answer_selections, ([layoutId, optionId]) => layoutId);
        answer_clone.text = getAnswerText(answer_clone.answer_selections);

        onCorrectAnswerChange(answer_clone);
        setAnswer(answer_clone);
        setDraggedItem(null);
    }

    const onRemove = (droppable_dnd_id) => (draggable_dnd_id) => () => {
        const answer_clone = _.cloneDeep(answer);
        _.remove(answer_clone.answer_selections, (pair) => pair[0] === droppable_dnd_id && pair[1] === draggable_dnd_id);
        answer_clone.text = getAnswerText(answer_clone.answer_selections);
        onCorrectAnswerChange(answer_clone);
        setAnswer(answer_clone);
    }

    function getDnDContainerStyle() {
        const bgImg = _.get(component_data, 'data.background_img');
        const width = _.get(component_data, 'config.width');
        const height = _.get(component_data, 'config.height');

        return {
            backgroundImage: `url(${bgImg})`,
            backgroundRepeat: 'no-repeat',
            width,
            height
        }
    }

    function findOptionData(answerLayoutId) {
        const pair = _.find(answer.answer_selections, (pair) => pair[0] === answerLayoutId);
        const option_dnd_id = _.get(pair, 1);
        if (!option_dnd_id) {
            return undefined;
        }
        const answer_options = _.get(component_data, 'data.answer_options');
        return _.find(answer_options, {dnd_id: option_dnd_id});
    }

    function renderLayout() {
        const answerLayout = _.get(component_data, 'data.answer_layout');
        const renderLayoutItems = answerLayout.map((item, key) => {
            switch (item.type) {
                case 'dnd':
                    return (
                        <DnDDroppableItem
                            key={key}
                            component_data={component_data}
                            item={item}
                            selected={findOptionData(item.dnd_id)}
                            onRemove={onRemove(item.dnd_id)}
                        />
                    );
                case 'static':
                default:
                    return null;
            }
        });

        return (
            <div className="cc-dndpuzzle-layout">
                {renderLayoutItems}
            </div>
        );
    }

    function renderOverlay() {
        if (dragged_item) {
            const answerLayout = _.get(component_data, 'data.answer_layout');
            const item = answerLayout.find(item => item.dnd_id === dragged_item);

            return (
                <DnDDraggedItem item={item} is_preview={true}/>
            );
        }

        return null;
    }

    return (
        <div className="challenge-component-dndpuzzle">
            <DndContext
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                sensors={sensors}
                collisionDetection={rectIntersection}
            >
                <AnswerOptions component_data={component_data}/>

                <div className="cc-dndpuzzle-scroll">
                    <div
                        className="cc-dndpuzzle-bg"
                        style={getDnDContainerStyle()}
                    >
                        {renderLayout()}
                    </div>
                </div>

                <DragOverlay>
                    {renderOverlay()}
                </DragOverlay>
            </DndContext>
        </div>
    );
}

DragNDropPuzzle.propTypes = {
    component_data: PropTypes.shape({
        data: PropTypes.shape({
            answer_groups: PropTypes.arrayOf(PropTypes.shape({
                dnd_type: PropTypes.string.isRequired,
                direction: PropTypes.string.isRequired,
                options: PropTypes.arrayOf(PropTypes.shape({
                    dnd_id: PropTypes.string.isRequired,
                    html: PropTypes.string.isRequired,
                    text: PropTypes.string.isRequired
                })).isRequired,
                color_light: PropTypes.string,
                color_dark: PropTypes.string,
                // droppable_style: PropTypes.object,
                // draggable_style: PropTypes.object,
            })).isRequired,
            answer_layout: PropTypes.arrayOf(PropTypes.shape({
                type: PropTypes.string.isRequired,
                dnd_type: PropTypes.string,
                dnd_id: PropTypes.string,
                placeholder: PropTypes.string,
                // droppable_style: PropTypes.object,
                content: PropTypes.shape({
                    html: PropTypes.string.isRequired,
                    text: PropTypes.string.isRequired
                })
            })).isRequired
        }).isRequired
    }).isRequired,
    correct_answer: PropTypes.oneOfType([
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            answer_selections: PropTypes.array.isRequired
        }),
        PropTypes.array
    ]),
    onCorrectAnswerChange: PropTypes.func.isRequired
}

export default forwardRef(DragNDropPuzzle);
